import dayjs from 'dayjs';

import { CURRENCY_SYMBOLS, PeriodPreset, PERIOD_PRESETS } from '../constants/base';

import { endOfDay, startOfDay } from '@/utils/dates';

/**
 * Same cutoff as server CHECKS_REVIEW_STARTING_TIME (2024-03-04T01:00:00.000Z).
 * Must be the UTC instant — not local startOf('day') — otherwise "Всё" opens a
 * pre-review window where checks are selected by date without approval filter.
 */
export const CHECKS_REVIEW_STARTING_TIME = 1709514000000;
export const ALL_PERIOD_FROM = new Date(CHECKS_REVIEW_STARTING_TIME);

export const getPresetRange = (preset: PeriodPreset): [Date, Date] => {
  const to = endOfDay(new Date());
  if (preset === 'all') {
    return [ALL_PERIOD_FROM, to];
  }
  const days = PERIOD_PRESETS.find((item) => item.value === preset)?.days || 30;
  const from = startOfDay(
    dayjs()
      .subtract(days - 1, 'day')
      .toDate()
  );
  return [from, to];
};

export const detectPreset = (from: Date | null, to: Date | null): PeriodPreset | null => {
  if (!from || !to) {
    return null;
  }
  return (
    PERIOD_PRESETS.find((preset) => {
      const [presetFrom, presetTo] = getPresetRange(preset.value);
      // "Всё" is a fixed UTC instant — day-equality would mismatch west of UTC.
      if (preset.value === 'all') {
        return from.valueOf() === presetFrom.valueOf() && dayjs(to).isSame(presetTo, 'day');
      }
      return dayjs(from).isSame(presetFrom, 'day') && dayjs(to).isSame(presetTo, 'day');
    })?.value || null
  );
};

/** Period echoed back by the report — captions must follow the data, not the pickers. */
export const toPeriodDates = (period: {
  dateFrom: string | number;
  dateTo: string | number;
}): [Date, Date] => {
  const parse = (value: string | number) =>
    typeof value === 'string'
      ? dayjs(value, 'YYYY-MM-DD').startOf('day').toDate()
      : new Date(value);
  return [parse(period.dateFrom), parse(period.dateTo)];
};

export const formatPeriodCaption = (from: Date, to: Date): string => {
  const days = dayjs(to).startOf('day').diff(dayjs(from).startOf('day'), 'day') + 1;
  const isWithYear = periodCrossesYear(from, to);
  const fmt = isWithYear ? 'DD.MM.YYYY' : 'DD.MM';
  return `${dayjs(from).format(fmt)} — ${dayjs(to).format(fmt)} · ${days} дн`;
};

export const periodCrossesYear = (from: Date, to: Date): boolean => {
  return dayjs(from).year() !== dayjs(to).year();
};

export const formatChartDate = (date: string, isWithYear = false): string => {
  return dayjs(date).format(isWithYear ? 'DD.MM.YY' : 'DD.MM');
};

export const getCurrencySymbol = (currency: string | null | undefined): string => {
  if (!currency) {
    return '';
  }
  return CURRENCY_SYMBOLS[currency] || currency;
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value);
};

export const formatMoney = (value: number, currency: string | null | undefined): string => {
  const symbol = getCurrencySymbol(currency);
  return symbol ? `${formatNumber(value)} ${symbol}` : formatNumber(value);
};

export const formatShare = (share: number): string => {
  const pct = share * 100;
  // Sub-0.1% rows (incl. 0.057% “крадник”) must not surface as "0.1%" / "0.04%".
  if (!Number.isFinite(pct) || Math.abs(pct) < 0.1) {
    return '0%';
  }
  return `${pct.toFixed(1).replace(/\.0$/, '')}%`;
};

/**
 * Ceil `max` to a round axis top so Recharts ticks land on nice steps
 * (e.g. 2121 → 4000 → ticks 0 / 1k / 2k / 3k / 4k with tickCount 5).
 */
export const getNiceAxisMax = (max: number, tickCount = 5): number => {
  const segments = Math.max(1, tickCount - 1);
  if (!Number.isFinite(max) || max <= 0) {
    return segments;
  }
  const roughStep = max / segments;
  const exp = Math.floor(Math.log10(roughStep));
  const pow = 10 ** exp;
  const err = roughStep / pow;
  // 3 and 4 keep mock-round tops (12000 → 12000, ticks 0/3k/6k/9k/12k) instead
  // of jumping straight from 5 to 10 and leaving the chart half-empty.
  const steps = [1, 2, 2.5, 3, 4, 5, 10];
  const niceErr = steps.find((step) => err <= step) ?? 10;
  return niceErr * pow * segments;
};

/**
 * Compact Y-axis labels for revenue charts.
 * `scaleMax` keeps the whole axis in one unit: when the series reaches ≥1k,
 * sub-1k ticks render as 0.6k instead of mixing "550" with "1k".
 * One decimal avoids collapsing nearby ticks (1652 & 2121 both used to become "2k").
 */
export const formatCompactAxis = (value: number, scaleMax = 0): string => {
  if (!Number.isFinite(value) || value === 0) {
    return '0';
  }
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const useK = Math.max(scaleMax, abs) >= 1000;

  if (!useK) {
    return `${sign}${Math.round(abs)}`;
  }

  const rounded = Math.round((abs / 1000) * 10) / 10;
  if (Number.isInteger(rounded)) {
    return `${sign}${rounded}k`;
  }
  return `${sign}${rounded.toFixed(1)}k`;
};

export const formatNullableCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return formatNumber(value);
};
